import React, { createContext, useContext, useReducer, useState } from 'react';
import { getResourceId } from '../utils/get-resource-id';
import { reducer } from './reducer';
import {
  ActionType,
  type ContentBlock,
  type ManagePathContextProps,
} from './types';
import {
  ResourceTypes,
  type HeaderImage,
  type LearningPath,
  type ResourceGroup,
} from '@/data/learning-path/interfaces';

const ManagePathContext = createContext<ManagePathContextProps | null>(null);

interface ManagePathProviderProps {
  children: React.ReactNode;
  currentPath?: LearningPath;
}

export const ManagePathProvider = ({
  children,
  currentPath,
}: ManagePathProviderProps) => {
  const pathId = currentPath?.id || '';
  const image = currentPath?.header_image?.url ?? '';
  const [contents, rawDispatch] = useReducer(
    reducer,
    currentPath?.contents || []
  );
  const [title, rawSetTitle] = useState(currentPath?.title || '');
  const [description, rawSetDescription] = useState(
    currentPath?.description || ''
  );
  const [headerImage, setHeaderImage] = useState<HeaderImage | null>(
    image
      ? {
          cropArea: { x: 0, y: 0, width: 320, height: 128 },
          previewUrl: image,
          blob: currentPath?.header_image?.blob ?? '',
        }
      : null
  );
  const isGlobal = currentPath?.global;
  const [hasImageChanged, setHasImageChanged] = useState(false);
  const [hasContentChanged, setHasContentChanged] = useState(false);
  const [isDraftVersion, setIsDraftVersion] = useState(
    currentPath?.draft_version
  );
  const [alreadyPublished, setAlreadyPublished] = useState(
    currentPath?.has_published_version
  );

  const dispatch: typeof rawDispatch = (action) => {
    rawDispatch(action);
    setHasContentChanged(true);
  };

  const setTitle = (value: string) => {
    rawSetTitle(value);
    setHasContentChanged(true);
  };

  const setDescription = (value: string) => {
    rawSetDescription(value);
    setHasContentChanged(true);
  };

  const handleSetHeaderImage = (file: HeaderImage | null) => {
    setHeaderImage(file);
    setHasImageChanged(true);
    setHasContentChanged(true);
  };

  const currentResourceBlock = (blockId: string) => {
    const block = contents.find((content) => content.id === blockId);
    if (!block || block.type !== ResourceTypes.ResourceGroup) return;
    return block;
  };

  const addTextBlock = () => {
    dispatch({
      type: ActionType.AddBlock,
      payload: {
        type: ResourceTypes.Text,
        content: { body: '' },
      },
    });
  };

  const addResourcesBlock = () => {
    dispatch({
      type: ActionType.AddBlock,
      payload: {
        type: ResourceTypes.ResourceGroup,
        content: { resource_groups: [] },
      },
    });
  };

  const updateBlock = (id: string, data: Partial<ContentBlock>) => {
    dispatch({
      type: ActionType.UpdateBlock,
      payload: { id, data },
    });
  };

  const removeBlock = (id: string) => {
    dispatch({
      type: ActionType.RemoveBlock,
      payload: { id },
    });
  };

  const addResourceToBlock = (blockId: string, resource: ResourceGroup) => {
    const block = currentResourceBlock(blockId);
    const currentResources = block?.content?.resource_groups ?? [];

    updateBlock(blockId, {
      content: {
        resource_groups: [...currentResources, resource],
      },
    });
  };

  const updateResourceInBlock = (
    blockId: string,
    updatedResource: ResourceGroup
  ) => {
    const block = currentResourceBlock(blockId);
    const updatedResources = block?.content?.resource_groups?.map((resource) =>
      getResourceId(resource) === getResourceId(updatedResource)
        ? updatedResource
        : resource
    );

    updateBlock(blockId, {
      content: { resource_groups: updatedResources ?? [] },
    });
  };

  const removeResourceFromBlock = (blockId: string, resourceId: string) => {
    const block = currentResourceBlock(blockId);
    const filtered = block?.content?.resource_groups?.filter(
      (resource) => getResourceId(resource) !== resourceId
    );

    updateBlock(blockId, {
      content: { resource_groups: filtered ?? [] },
    });
  };

  const reorderBlocks = (blocks: ContentBlock[]) => {
    dispatch({
      type: ActionType.ReorderBlocks,
      payload: blocks.map((block, index) => ({ ...block, position: index })),
    });
  };

  return (
    <ManagePathContext.Provider
      value={{
        contents,
        headerImage: headerImage ?? {
          cropArea: { x: 0, y: 0, width: 320, height: 128 },
          previewUrl: '',
          blob: '',
        },
        pathId,
        title,
        description,
        isGlobal: isGlobal ?? false,
        hasContentChanged,
        setHasContentChanged,
        hasImageChanged,
        isDraftVersion: isDraftVersion ?? false,
        setIsDraftVersion,
        alreadyPublished: alreadyPublished ?? false,
        setAlreadyPublished,
        setTitle,
        setDescription,
        addTextBlock,
        addResourcesBlock,
        addResourceToBlock,
        updateBlock,
        updateResourceInBlock,
        removeBlock,
        removeResourceFromBlock,
        setHeaderImage: handleSetHeaderImage,
        reorderBlocks,
      }}
    >
      {children}
    </ManagePathContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useManagePath = () => {
  const context = useContext(ManagePathContext);
  if (!context)
    throw new Error('useManagePath must be used within a ManagePathProvider');
  return context;
};
